import { useEffect, useMemo, useState } from "react"
import { useIntl } from "react-intl"

import { SearchInput } from "@doktor-se/bones-ui"
import { Role, Staff } from "@doktor-se/bones-ui/dist/web-shared/types"

import { defaultRole } from "config"

import FilterDropdown, { FilterOption } from "../FilterDropdown/FilterDropdown"
import UserRow from "../UserRow/UserRow"

import styles from "./StaffSearch.module.scss"

const LOCAL_STORAGE_KEY = "STAFF_REASSIGN_FAVORITES"

export const saveStaffFavoritesToLocalStorage = (ids: string[]) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(ids))
}

export const retrieveStaffFavoritesFromLocalStorage = (): Set<string> => {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
  if (!stored) return new Set()
  return new Set(JSON.parse(stored))
}

interface StaffSearchProps {
  staffList: Staff[]
  onSelectStaff: (staff: Staff) => void
}

const filterOptions: FilterOption[] = [
  { label: "label.reassign.availability", value: "availability" },
  { label: "label.reassign.favorites", value: "favorites" },
  { label: "label.reassign.name", value: "name" },
  { label: "label.reassign.role", value: "role" }
]

const StaffSearch = ({ staffList, onSelectStaff }: StaffSearchProps) => {
  const intl = useIntl()
  const [filterQuery, setFilter] = useState("")
  const [selectedFilterOption, setSelectedFilterOption] = useState<FilterOption>(filterOptions[0])
  const [favorites, setFavorites] = useState<Set<string>>(new Set())

  useEffect(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (stored) {
      setFavorites(new Set(JSON.parse(stored)))
    }
  }, [])

  const saveFavoritesToLocalStorage = (updated: Set<string>) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(Array.from(updated)))
  }

  const toggleFavorite = (id: string) => {
    const updated = new Set(favorites)
    if (updated.has(id)) {
      updated.delete(id)
    } else {
      updated.add(id)
    }

    setFavorites(updated)
    saveFavoritesToLocalStorage(updated)
  }

  const isFavorite = (id: string) => favorites.has(id)

  const filteredStaffList = useMemo(
    () =>
      staffList
        .filter(p => p.displayName.toLowerCase().includes(filterQuery.toLowerCase()))
        .sort((a, b) => {
          switch (selectedFilterOption.value) {
            case "availability":
              return Number(b.online) - Number(a.online)
            case "favorites":
              const aIsFav = favorites.has(a.id)
              const bIsFav = favorites.has(b.id)
              if (aIsFav && !bIsFav) return -1
              if (!aIsFav && bIsFav) return 1
              return 0
            case "name":
              return a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
            case "role":
              const defaultRoleA = defaultRole(a.roles as Role[])
              const defaultRoleB = defaultRole(b.roles as Role[])
              return defaultRoleA && defaultRoleB
                ? defaultRoleA.toLowerCase().localeCompare(defaultRoleB.toLowerCase())
                : 0
            default:
              return 0
          }
        }),
    [staffList, filterQuery, selectedFilterOption.value, favorites]
  )

  return (
    <div className={styles.container}>
      <div className={styles.searchContainer}>
        <SearchInput
          variant="onSurface"
          aria-label={intl.formatMessage({ id: "input.search.staff.aria.label" })}
          value={filterQuery}
          onChange={value => setFilter(value)}
        />
      </div>

      <FilterDropdown options={filterOptions} selected={selectedFilterOption} onChange={setSelectedFilterOption} />

      <ol className={styles.list}>
        {filteredStaffList.map((staff, index) => (
          <UserRow
            user={staff}
            key={staff.id}
            onClick={() => onSelectStaff(staff)}
            tabIndex={index}
            isFavorite={isFavorite(staff.id)}
            onFavoriteClick={toggleFavorite}
          />
        ))}
      </ol>
    </div>
  )
}

export default StaffSearch
